import { api, attachAuthOptions, authOptions } from '../../utils/api';
import {
  EditFolderSchema,
  FolderSchema, RenameFileSchema, ReplaceFileSchema, UploadFileSchema,
} from './Forms/schemas';

const FILE_EXPLORER_URL = 'filetree';

interface GetFilesParams {
  id?: number
}

export interface Permissions {
  can_read: boolean,
  can_write: boolean,
  can_delete: boolean,
  can_update: boolean,
}

export interface TreeFolder {
  id: number;
  name: string;
  description: string;
  remove_file_prefix: boolean;
  icon?: {
    type: string;
    name: string;
    path?: string;
  };
  permissions: Permissions;
  shareable_link: string;
}

export interface TreeFile {
  id: number;
  category: number;
  notify_users: boolean;
  visible_in_global_search: boolean;
  description: string;
  title: string;
  file: string;
  url: string;
  [key: string]: string | number | boolean
  updated_at: string;
}

export interface GetFilesResponse {
  path: string;
  folders: TreeFolder[];
  files: TreeFile[]
}

export async function getFiles(params: GetFilesParams) {
  const { id } = params;
  const searchParams = id ? { id } : {};
  return api.get<GetFilesResponse>(FILE_EXPLORER_URL, { params: { ...searchParams } });
}

// Folder mutations

export async function createFolder(data: FolderSchema) {
  const response = await api.post<TreeFolder>(`${FILE_EXPLORER_URL}/category/`, data, attachAuthOptions());
  return response.data;
}

export async function editFolder(id: number, data: EditFolderSchema) {
  return api.put(`${FILE_EXPLORER_URL}/category/${id}/`, data, attachAuthOptions());
}

export async function deleteFolder(id: number) {
  return api.delete(`${FILE_EXPLORER_URL}/category/${id}/`, attachAuthOptions());
}

// File mutations

export async function deleteFile(id: number) {
  return api.delete(`${FILE_EXPLORER_URL}/file-resource/${id}/`, attachAuthOptions());
}

export async function renameFile(id: number, data: RenameFileSchema) {
  // Since patch endpoint only supports form data, we need to create a form data object
  // This is because the patch endpoint can be used to update a file.
  const formData = new FormData();
  formData.append('title', data.title);
  return api.patch(`${FILE_EXPLORER_URL}/file-resource/${id}/`, formData, {
    ...authOptions,
    headers: {
      ...authOptions.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
}

export async function uploadFiles(data: UploadFileSchema) {
  const formData = new FormData();
  formData.append('category', data.category.toString());

  // 'backup' means save backup → backup_existing=true
  // 'replace' means replace → backup_existing=false
  const shouldBackup = data.duplicateAction === 'backup';
  formData.append('backup_existing', (shouldBackup).toString());

  if (data.files.length === 1) {
    formData.append('file', data.files[0]);
    formData.append('title', data.filesMeta?.[0]?.title || '');
    formData.append('description', data.filesMeta?.[0]?.description || '');
  } else {
    data.files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const endpoint = data.files.length === 1 ? 'file-resource/' : 'file-resource/bulk';

  const response = await api.post<{ count: number, created_files: TreeFile[], message:string }>(`${FILE_EXPLORER_URL}/${endpoint}`, formData, {
    ...authOptions,
    headers: {
      ...authOptions.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
  if ('created_files' in response.data) {
    return response.data.created_files;
  }
  return [response.data];
}

export async function replaceFile(id: number, data: ReplaceFileSchema) {
  const formData = new FormData();
  formData.append('title', data.title);
  formData.append('description', data.description || '');
  if (data.file) {
    formData.append('file', data.file);
  }

  return api.patch(`${FILE_EXPLORER_URL}/file-resource/${id}/`, formData, {
    ...authOptions,
    headers: {
      ...authOptions.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
}

export interface ValidatedFile {
  original_filename: string;
  formatted_title: string;
  is_duplicate: boolean;
  existing_file?: TreeFile;
}

export interface ValidateFilesResponse {
  validated_files: ValidatedFile[];
  duplicates: ValidatedFile[];
  has_duplicates: boolean;
}

export async function validateFiles(filenames: string[], categoryId: number) {
  const response = await api.post<ValidateFilesResponse>(
    `${FILE_EXPLORER_URL}/file-resource/validate`,
    {
      filenames,
      category: categoryId,
    },
    {
      ...authOptions,
      headers: {
        ...authOptions.headers,
      },
    }
  );
  return response.data;
}
